import React, { useState, type ReactNode, type KeyboardEvent } from 'react';
import { NudgeSpotlight } from '@atlassiansox/nudge-tooltip';
import { SpotlightPulse } from '@atlaskit/onboarding';
import { Box, xcss } from '@atlaskit/primitives';
import { useShouldNestedElementRender } from '@atlaskit/side-navigation';
import { layers } from '@atlaskit/theme/constants';
import { withChoreographedToggle } from '@atlassian/jira-choreographer-services/src/main.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useSeenStatus } from '@atlassian/jira-servicedesk-itsm-onboarding-common/src/controllers/seen-status/index.tsx';
import { usePageContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-context/main.tsx';
import { createOnboardingSpotlight } from '@atlassian/jira-software-onboarding-nudges--next/src/common/ui/onboarding-spotlight/index.tsx';
import { JSM_VIEWS_NAV_ONBOARDING_KEY, packageName, teamName } from '../../common/constants.tsx';
import { MergedViewsIllustration } from './merged-views-illustration/index.tsx';
import messages from './messages.tsx';

type SpotlightProps = {
	setShouldShowSpotlight: (shouldShowSpotlight: boolean) => void;
	children: ReactNode;
};

type ChoreographedSpotlightProps = SpotlightProps & {
	isChoreographingStarted?: boolean;
};

const screenName = 'jsmViewsOnboardingSpotlight';

const ChoreographedToggleableSpotlight = withChoreographedToggle<ChoreographedSpotlightProps>(
	({
		isChoreographingStarted = true,
		setShouldShowSpotlight,
		children,
	}: ChoreographedSpotlightProps) => {
		const { formatMessage } = useIntl();
		const [{ data: pageContextData }] = usePageContext();
		const { projectId, projectType } = pageContextData?.projectContext || {};
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const onDismiss = () => {
			setShouldShowSpotlight(false);
		};

		const onReadMore = () => {
			onDismiss();

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.open(
				'https://support.atlassian.com/jira-service-management-cloud/docs/enable-new-views-for-your-service-project/',
				'_blank',
				'noreferrer',
			);
		};

		const renderNudge = () =>
			createOnboardingSpotlight({
				actions: [
					{
						onClick: () => {
							fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', `${screenName}ReadMore`);
							onReadMore();
						},
						content: formatMessage(messages.readMore),
						appearance: 'subtle',
					},
					{
						onClick: () => {
							fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', `${screenName}Dismiss`);
							onDismiss();
						},
						content: formatMessage(messages.ok),
						appearance: 'primary',
					},
				],
				contentMessage: formatMessage(messages.description),
				image: <MergedViewsIllustration />,
				headingMessage: formatMessage(messages.heading),
			});

		if (!isChoreographingStarted) {
			return children;
		}

		return (
			<ContextualAnalyticsData
				sourceType={SCREEN}
				sourceName={screenName}
				attributes={{
					messageId: JSM_VIEWS_NAV_ONBOARDING_KEY,
					name: screenName,
					projectId,
					projectType,
				}}
			>
				<NudgeSpotlight
					hidden={false}
					setHidden={() => {
						/* Handled via onDismissClick */
					}}
					onOutsideClick={() => {
						onDismiss();
						fireUIAnalytics(createAnalyticsEvent({}), 'blanket clicked', `${screenName}Dismiss`);
					}}
					spotlightCard={renderNudge()}
					concealSpotlightOnReferenceHidden
					position="right-start"
					zIndex={layers.dialog()}
				>
					<Box>{children}</Box>
				</NudgeSpotlight>
				<FireScreenAnalytics />
			</ContextualAnalyticsData>
		);
	},
	'isChoreographingStarted',
);

const ChoreographedSpotlight = ({
	isChoreographingStarted = true,
	...props
}: ChoreographedSpotlightProps) => {
	return (
		<ChoreographedToggleableSpotlight
			{...props}
			isChoreographingStarted={isChoreographingStarted}
			messageId={JSM_VIEWS_NAV_ONBOARDING_KEY}
		/>
	);
};

const Spotlight = (props: SpotlightProps) => <ChoreographedSpotlight {...props} />;

const Pulse = ({
	children,
	shouldShowPulse,
	setShouldShowSpotlight,
	markSpotlightAsSeen,
}: {
	children: ReactNode;
	shouldShowPulse: boolean;
	setShouldShowSpotlight: (shouldShowSpotlight: boolean) => void;
	markSpotlightAsSeen: () => void;
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClick = () => {
		fireUIAnalytics(createAnalyticsEvent({}), 'pulse clicked', `${screenName}Pulse`);
		setShouldShowSpotlight(true);
		markSpotlightAsSeen();
	};

	return (
		<Box xcss={pulseContainerStyles}>
			<SpotlightPulse pulse={shouldShowPulse} radius={3}>
				<Box
					as="span"
					role="button"
					aria-label={formatMessage(messages.pulseLabel)}
					onClick={onClick}
					xcss={pulseTargetContainerStyles}
					onKeyDown={(e: KeyboardEvent) => {
						e.key === 'Enter' && onClick();
					}}
					tabIndex={0}
					data-testId="servicedesk-views-onboarding.ui.views-nav-item-onboarding.spotlight-trigger"
				>
					{children}
				</Box>
			</SpotlightPulse>
		</Box>
	);
};

const ViewsNavItemOnboardingView = ({ children }: { children: ReactNode }) => {
	const [{ isSeen: hasSeenSpotlight }, { markAsSeen: markSpotlightAsSeen }] = useSeenStatus({
		userPropertyKey: JSM_VIEWS_NAV_ONBOARDING_KEY,
		isSeenValue: true,
		packageName,
		teamName,
	});
	const shouldShowPulse = !hasSeenSpotlight;
	const [shouldShowSpotlight, setShouldShowSpotlight] = useState(false);

	return (
		<>
			{shouldShowPulse && (
				<Pulse
					setShouldShowSpotlight={setShouldShowSpotlight}
					markSpotlightAsSeen={markSpotlightAsSeen}
					shouldShowPulse={shouldShowPulse}
				>
					<Box>{children}</Box>
				</Pulse>
			)}
			{shouldShowSpotlight && (
				<Spotlight setShouldShowSpotlight={setShouldShowSpotlight}>{children}</Spotlight>
			)}
			{!shouldShowPulse && !shouldShowSpotlight && children}
		</>
	);
};

export const ViewsNavItemOnboarding = ({ children }: { children: ReactNode }) => {
	const { shouldRender } = useShouldNestedElementRender();
	if (!getWillShowNav4() && !shouldRender) {
		return children;
	}

	return (
		<JSErrorBoundary
			id="jiraServicedeskViewsNavItemOnboarding"
			packageName={packageName}
			teamName={teamName}
			fallback={() => children}
		>
			<ViewsNavItemOnboardingView>{children}</ViewsNavItemOnboardingView>
		</JSErrorBoundary>
	);
};

const pulseContainerStyles = xcss({
	position: 'relative',
});

const pulseTargetContainerStyles = xcss({
	cursor: 'pointer',
});
